import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, controlToastState, openModal } from "risa-oasis-ui_v2";
import { updateReports } from "../../../../api/postCall/updateReports";
import TriggerRpaNewModal from "../../../../components/modals/triggerRpaNewModal/triggerRpaNewModal";
import { authStatusForDisablingTriggerRpa } from "../../../../constants/authStatusForDisablingTriggerRpa";
import { useBusniessOfficeNotesContext } from "../../../../context/busniessOfficeNotesContext";
import { usePayerNameContextFromSingleOrderLayout } from "../../../../context/payerNameContextFromSingleOrderLayout";
import { AuthOnFileRpaErrorType } from "../../../../enums/authOnFileRpaErrorType";
import { BusniessOfficeReportType } from "../../../../enums/busniessOfficeReportType";
import { DropdownLocation } from "../../../../enums/dropdownLocation";
import { EVWriteBackStatus } from "../../../../enums/evBvWriteBackStatus";
import { ScreenNamesForBigQuery } from "../../../../enums/eventsData";
import { EventsName } from "../../../../enums/eventsName";
import { LocalStorageKeys } from "../../../../enums/localStorageKeys";
import { ModalId } from "../../../../enums/modalId";
import { useAuthOnFileError } from "../../../../hooks/useAuthOnFileError";
import {
  closeModal,
  setOpenedModalId,
} from "../../../../redux/slice/modalSlice";
import { AppDispatch, RootState } from "../../../../redux/store/store";
import { setItemInLocalStorage } from "../../../../utils/localStorageHelper";
import { getOrgIdForFetchExternalWorklist } from "../../../../utils/organizationHelper";
import { saveCigBoxNoteChanges } from "../../../../utils/saveCigBoxNoteChanges";
import { apiWorkerManager } from "../../../../workerManagers/workerManager";
import { getAllJCodes } from "../../utils/existingDocsToLs";
import StatusError from "../errorComponents/statusError";
import AllComments from "./components/allComments";

const BusniessOffice = () => {
  const { singleMedicalPaOrder } = useSelector(
    (state: RootState) => state.singleMedicalPaOrder,
  );

  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );

  const { openedModalId } = useSelector((state: RootState) => state.modal);
  const {
    setAllCommentsForUI,
    allCommentsForUI,
    hasCptTableAnyError,
    shouldDisableMarkAsComplete,
    globalBoValue,
  } = useBusniessOfficeNotesContext();

  const [loading, setLoading] = useState(false);
  const [saveBoLoading, setSaveBoLoading] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const [rpaTriggered, setRpaTriggered] = useState(false);
  const lastExecutionTimeRef = useRef<number>(0);
  const { payerName } = usePayerNameContextFromSingleOrderLayout();
  const handleTriggerRpa = () => {
    apiWorkerManager.enqueueAndProcess({
      patient_id: singleMedicalPaOrder?.patient_id ?? "",
      order_id: singleMedicalPaOrder?.order_id ?? "",
      org_id: getOrgIdForFetchExternalWorklist() || "",
      event_name: EventsName.TRIGGER_RPA_CLICKED,
      user_id: user?.id ?? "",
      user_email: user?.email ?? "",
      additional_data: {
        screen_name: ScreenNamesForBigQuery.BUSNIESS_OFFICE_SCREEN,
        payer_name: payerName,
      },
    });
    dispatch(
      setOpenedModalId({
        id: ModalId.TRIGGER_RPA_MODAL_NEW,
      }),
    );
  };

  const handleSaveBoNotes = async () => {
    setSaveBoLoading(true);
    try {
      apiWorkerManager.enqueueAndProcess({
        patient_id: singleMedicalPaOrder?.patient_id ?? "",
        order_id: singleMedicalPaOrder?.order_id ?? "",
        org_id: getOrgIdForFetchExternalWorklist() || "",
        event_name: EventsName.SAVE_BO_NOTES_CLICKED,
        user_id: user?.id ?? "",
        user_email: user?.email ?? "",
        additional_data: {
          screen_name: ScreenNamesForBigQuery.BUSNIESS_OFFICE_SCREEN,
          payer_name: payerName,
          j_codes: getAllJCodes(singleMedicalPaOrder ?? {}),
          bo_value: globalBoValue,
          comments: allCommentsForUI,
        },
      });

      // Extract post comments to create reports array
      const postComments = allCommentsForUI.filter(
        (comment) => comment.type === BusniessOfficeReportType.FINAL,
      );

      const reports = postComments.flatMap((commentGroup) =>
        commentGroup.comments
          .filter((comment) => comment.boStatus && comment.writeBackComment)
          .map((comment) => ({
            report_identifier: comment.id,
            template_text: comment.writeBackComment || "",
            report_status: comment.boStatus || "",
          })),
      );

      const payload = {
        identifier: singleMedicalPaOrder?.id ?? "",
        reports: reports,
        org_id: getOrgIdForFetchExternalWorklist() || "",
      };

      const response = await updateReports(payload);
      if (response.status === "success") {
        controlToastState("bo-notes-saved-success");
      } else {
        controlToastState("bo-notes-saved-failure");
      }
    } catch (error) {
      console.error("Error saving BO notes:", error);
      controlToastState("bo-notes-saved-failure");
    } finally {
      setSaveBoLoading(false);
    }
  };

  const handleBoStatusChange = (data: { id: string; status: string }) => {
    setAllCommentsForUI((prevComments) =>
      prevComments.map((commentGroup) => {
        if (commentGroup.type === BusniessOfficeReportType.FINAL) {
          return {
            ...commentGroup,
            comments: commentGroup.comments.map((comment) =>
              comment.id === data.id
                ? { ...comment, boStatus: data.status }
                : comment,
            ),
          };
        }

        return commentGroup;
      }),
    );
  };
  const handleWriteBackCommentChange = (data: {
    id: string;
    comment: string;
  }) => {
    setAllCommentsForUI((prevComments) =>
      prevComments.map((commentGroup) => {
        if (commentGroup.type === BusniessOfficeReportType.FINAL) {
          return {
            ...commentGroup,
            comments: commentGroup.comments.map((comment) =>
              comment.id === data.id
                ? { ...comment, writeBackComment: data.comment }
                : comment,
            ),
          };
        }

        return commentGroup;
      }),
    );
  };

  const handleTriggerRpaButtonDisabledState = useMemo(() => {
    if (loading || rpaTriggered || hasCptTableAnyError) {
      return true;
    }

    if (allCommentsForUI.length === 0) {
      return true;
    }

    const getOnlyPostComments = allCommentsForUI.filter(
      (comment) => comment.type === BusniessOfficeReportType.FINAL,
    );

    if (getOnlyPostComments.length === 0) {
      return true;
    }

    const isBoStatusPresent = getOnlyPostComments[0].comments.every(
      (comment) => comment.boStatus && comment.boStatus !== "",
    );

    if (!isBoStatusPresent) {
      return true;
    }

    if (
      singleMedicalPaOrder?.status?.ev_write_back_status ===
      EVWriteBackStatus.IN_PROGRESS
    ) {
      return true;
    }

    const isJCodePresent = getAllJCodes(singleMedicalPaOrder ?? {});
    if (!isJCodePresent || isJCodePresent?.length === 0) {
      return true;
    }

    return false;
  }, [loading, allCommentsForUI, singleMedicalPaOrder]);

  useEffect(() => {
    if (openedModalId === ModalId.TRIGGER_RPA_MODAL_NEW) {
      openModal(ModalId.TRIGGER_RPA_MODAL_NEW);
    }
  }, [openedModalId]);

  useEffect(() => {
    if (openedModalId === ModalId.COMMENT_RPA_MODAL_NEW) {
      openModal(ModalId.COMMENT_RPA_MODAL_NEW);
    }
  }, [openedModalId]);

  useEffect(() => {
    if (openedModalId === ModalId.HAPPY_FLOW_MODAL) {
      openModal(ModalId.HAPPY_FLOW_MODAL);
    }
  }, [openedModalId]);

  useEffect(() => {
    if (allCommentsForUI.length > 0) {
      setItemInLocalStorage(
        `${LocalStorageKeys.BO_COMMENTS}_${singleMedicalPaOrder?.id}`,
        allCommentsForUI,
      );
    }
  }, [allCommentsForUI]);

  const handleSaveChanges = async () => {
    const currentTime = Date.now();
    const timeSinceLastExecution = currentTime - lastExecutionTimeRef.current;

    if (timeSinceLastExecution < 10000) {
      controlToastState("retry-error");
      return;
    }

    lastExecutionTimeRef.current = currentTime;

    setLoading(true);
    try {
      // Auto-resolve saveCigBoxNoteChanges after 5 seconds
      await Promise.race([
        saveCigBoxNoteChanges({
          allCommentsForUI,
          patientMrn: singleMedicalPaOrder?.demographics?.patient_mrn ?? "",
          drugName: singleMedicalPaOrder?.payload?.regimen_name ?? "",
          orderId: singleMedicalPaOrder?.id ?? "",
          org_id: getOrgIdForFetchExternalWorklist() || "",
          bo_value: globalBoValue,
          userEmail: user?.email ?? "",
          userId: user?.id ?? "",
          payerName: payerName,
        }),
        new Promise((resolve) =>
          setTimeout(() => resolve({ success: true }), 2000),
        ),
      ]);
      setRpaTriggered(true);
      controlToastState("cig-box-note-updated-success");
      dispatch(closeModal());
    } catch (error) {
      controlToastState("cig-box-note-updated-failure");
    } finally {
      setLoading(false);
    }
  };

  const shouldHideTriggerRpaButton = useMemo(() => {
    return authStatusForDisablingTriggerRpa.includes(
      singleMedicalPaOrder?.auth_on_file?.auth_status ?? "",
    );
  }, [singleMedicalPaOrder?.auth_on_file?.auth_status]);

  const sortedCommentsForUI = useMemo(() => {
    return [...allCommentsForUI].sort((a, b) => {
      const dateA = new Date(a.commentDate);
      const dateB = new Date(b.commentDate);
      // Sort descending (latest first)
      return dateB.getTime() - dateA.getTime();
    });
  }, [allCommentsForUI]);

  const {
    shouldShowError: shouldShowBusinessOfficeRowsNotFound,
    errorMessage,
    dropdownLocation,
  } = useAuthOnFileError(
    singleMedicalPaOrder,
    AuthOnFileRpaErrorType.BUSINESS_OFFICE_ROWS_NOT_FOUND,
  );

  const shouldShowDropdownInBo =
    dropdownLocation === DropdownLocation.BUSINESS_OFFICE ||
    dropdownLocation === DropdownLocation.BOTH;

  if (shouldShowBusinessOfficeRowsNotFound) {
    return (
      <div className="busniess-office-container h-full">
        <StatusError
          title={errorMessage}
          showDropdown={shouldShowDropdownInBo}
        />
      </div>
    );
  }

  return (
    <div className="busniess-office-container">
      {!shouldHideTriggerRpaButton && !shouldDisableMarkAsComplete && (
        <div className="flex justify-end gap-2">
          <Button
            buttonType="tertiary"
            size="medium"
            disabled={handleTriggerRpaButtonDisabledState || saveBoLoading}
            onClick={handleSaveBoNotes}
          >
            {saveBoLoading ? "Saving..." : "Save BO Notes"}
          </Button>
          <Button
            buttonType="secondary"
            size="medium"
            disabled={handleTriggerRpaButtonDisabledState}
            onClick={handleTriggerRpa}
          >
            Trigger RPA
          </Button>
        </div>
      )}
      {sortedCommentsForUI.map((comment) => (
        <AllComments
          key={comment.commentDate}
          {...comment}
          onBoStatusChange={handleBoStatusChange}
          onWriteBackCommentChange={handleWriteBackCommentChange}
        />
      ))}

      {openedModalId === ModalId.TRIGGER_RPA_MODAL_NEW && (
        <TriggerRpaNewModal
          onTriggerRpa={handleSaveChanges}
          onClose={() => dispatch(closeModal())}
        />
      )}
    </div>
  );
};

export default BusniessOffice;
